import { useEffect, useState } from 'react';
import { listTaxonomies, TaxonomyDomain, TaxonomyItem } from '../services/taxonomiesService';
import { getInfluenceCategories, InfluenceCategory } from '../services/influencesService';

type Domain = Exclude<TaxonomyDomain, 'influence_category'> | 'influence_category';

interface TaxonomyState {
  items: Partial<Record<Domain, Array<TaxonomyItem | InfluenceCategory>>>;
  loaded: boolean;
}

// One shared fetch for the whole app; pages subscribe to it
let state: TaxonomyState = { items: {}, loaded: false };
let pending: Promise<void> | null = null;
const listeners = new Set<(s: TaxonomyState) => void>();

const DOMAINS: Array<Exclude<Domain, 'influence_category'>> = ['stakeholder_category', 'relationship_category', 'organization_type'];

async function load(): Promise<void> {
  const results = await Promise.allSettled([
    ...DOMAINS.map((domain) => listTaxonomies(domain)),
    getInfluenceCategories(),
  ]);
  const items: TaxonomyState['items'] = {};
  DOMAINS.forEach((domain, i) => {
    const r = results[i];
    if (r.status === 'fulfilled' && Array.isArray(r.value)) items[domain] = r.value;
  });
  const influence = results[DOMAINS.length];
  if (influence.status === 'fulfilled' && Array.isArray(influence.value)) items.influence_category = influence.value;
  state = { items, loaded: true };
  listeners.forEach((listener) => listener(state));
}

function ensureLoaded() {
  if (!pending) pending = load().catch(() => undefined);
  return pending;
}

/** Call after creating/editing taxonomies so every page picks up the new colours */
export function refreshTaxonomyColors() {
  pending = load().catch(() => undefined);
  return pending;
}

const normalise = (value: string) => value.trim().toLowerCase().replace(/[\s_]+/g, '-');

function find(domain: Domain, value: string | undefined | null) {
  if (!value) return undefined;
  const list = state.items[domain] || [];
  const v = normalise(value);
  return list.find((item) => normalise(item.key) === v || normalise(item.label) === v);
}

/**
 * Taxonomy colours set by admins in Categories. Values match by key or label.
 * `colorOf` returns undefined when no colour is set — show a neutral style then.
 */
export function useTaxonomyColors() {
  const [snapshot, setSnapshot] = useState(state);

  useEffect(() => {
    listeners.add(setSnapshot);
    ensureLoaded();
    if (state !== snapshot) setSnapshot(state);
    return () => {
      listeners.delete(setSnapshot);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    loaded: snapshot.loaded,
    colorOf: (domain: Domain, value: string | undefined | null) => find(domain, value)?.color || undefined,
    labelOf: (domain: Domain, value: string | undefined | null) => find(domain, value)?.label,
    items: (domain: Domain) => snapshot.items[domain] || [],
  };
}
